/**
 * Central helper for serving right-sized, modern-format images instead of the
 * full-resolution originals.
 *
 * Background: every object under `users_img/` is a ~1.26 MB PNG (measured
 * across 203,264 files, 262.9 GB total). The gallery renders those originals
 * into ~400 px tiles, so ~97% of every byte downloaded is discarded. Chat
 * avatars are worse — 0.8-1.35 MB PNGs served through the Firebase download
 * API, which always responds `private, max-age=0`, so they were re-downloaded
 * on every render.
 *
 * Strategy 1 (default): Cloudinary fetch delivery.
 *   https://res.cloudinary.com/<cloud>/image/fetch/<transform>/<encoded source>
 *   Cloudinary pulls the origin once, caches it, and serves a resized
 *   WebP/AVIF from its CDN. Nothing has to be pre-generated, so this needs no
 *   backfill — Cloud Storage egress drops to one origin pull per image that is
 *   actually viewed, and effectively zero thereafter.
 *
 *   Measured against the live account:
 *     original PNG                     1,236,977 B
 *     w_400,c_limit,f_auto,q_auto         29,200 B   (97.6% smaller)
 *     w_1024,c_limit,f_auto,q_auto       116,290 B   (90.6% smaller)
 *     chat avatar w_200                   11,826 B   (99.1% smaller)
 *
 * Strategy 2 (fallback): pre-generated GCS derivatives, written by
 *   `scripts/generate-derivatives.js` to `users_img/_derived/<name>_w400.webp`.
 *   Used when REACT_APP_CLOUDINARY_CLOUD_NAME is unset. Keeps the option of
 *   dropping Cloudinary without a code change.
 *
 * Both strategies are paired with `imgFallback` so an image that cannot be
 * transformed still renders from its original URL.
 *
 * Delivery URLs are public and unsigned — no API key or secret belongs in this
 * file or in any REACT_APP_* variable, which are baked into the public bundle.
 */

// Public identifier, safe to ship in the bundle (it appears in every delivery
// URL). Set in .env and in the Vercel project settings.
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "";

export const DERIVED_DIR = "_derived";

// Widths `scripts/generate-derivatives.js` produces, for the fallback path.
export const DERIVED_WIDTHS = [400, 1024];

const GCS_HOSTS = ["storage.googleapis.com", "firebasestorage.googleapis.com"];

function snapWidth(width) {
  for (const w of DERIVED_WIDTHS) {
    if (width <= w) return w;
  }
  return DERIVED_WIDTHS[DERIVED_WIDTHS.length - 1];
}

/**
 * `users_img/foo.webp` -> `users_img/_derived/foo_w400.webp`
 * Kept in sync with scripts/derived-path.js.
 */
export function derivedObjectPath(objectPath, width) {
  if (!objectPath) return null;
  const w = snapWidth(width);
  const lastSlash = objectPath.lastIndexOf("/");
  const dir = lastSlash === -1 ? "" : objectPath.slice(0, lastSlash);
  const file = lastSlash === -1 ? objectPath : objectPath.slice(lastSlash + 1);
  const dot = file.lastIndexOf(".");
  const stem = dot === -1 ? file : file.slice(0, dot);
  const prefix = dir ? `${dir}/${DERIVED_DIR}` : DERIVED_DIR;
  return `${prefix}/${stem}_w${w}.webp`;
}

function gcsDerivedUrl(parsed, width) {
  if (parsed.pathname.includes(`/${DERIVED_DIR}/`)) return null;

  if (parsed.host === "firebasestorage.googleapis.com") {
    const m = /^\/v0\/b\/([^/]+)\/o\/(.+)$/.exec(parsed.pathname);
    if (!m) return null;
    const derived = derivedObjectPath(decodeURIComponent(m[2]), width);
    return derived ? `https://storage.googleapis.com/${m[1]}/${derived}` : null;
  }

  const path = parsed.pathname.replace(/^\//, "");
  const slash = path.indexOf("/");
  if (slash === -1) return null;
  const derived = derivedObjectPath(path.slice(slash + 1), width);
  return derived
    ? `https://storage.googleapis.com/${path.slice(0, slash)}/${derived}`
    : null;
}

/**
 * Build a Cloudinary fetch URL.
 * `c_limit` never upscales, `f_auto` negotiates AVIF/WebP from the Accept
 * header, `q_auto` picks quality per image.
 */
function cloudinaryUrl(sourceUrl, width) {
  const transform = `w_${width},c_limit,f_auto,q_auto`;
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/fetch/${transform}/${encodeURIComponent(
    sourceUrl
  )}`;
}

/**
 * Map a stored image URL to an optimised rendition.
 *
 * Returns the input unchanged for anything we cannot transform (relative
 * /public assets, blob: previews, already-transformed URLs), so it is always
 * safe to wrap a src in this.
 *
 * @param {string} url   original image URL
 * @param {number} width intended CSS display width in px
 */
export function derivedUrl(url, width = 400) {
  if (!url || typeof url !== "string") return url;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    // Relative path served by the app host, not by Cloud Storage.
    return url;
  }

  // Never re-transform something already transformed.
  if (parsed.host === "res.cloudinary.com") return url;

  if (CLOUD_NAME) {
    // Cloudinary can fetch any publicly reachable URL, including the tokenised
    // Firebase download URLs used for chat avatars.
    if (parsed.protocol !== "https:") return url;
    if (parsed.pathname.includes(`/${DERIVED_DIR}/`)) return url;
    return cloudinaryUrl(url, width);
  }

  if (!GCS_HOSTS.includes(parsed.host)) return url;
  return gcsDerivedUrl(parsed, width) || url;
}

/** Grid/tile sized rendition. */
export function thumbUrl(url) {
  return derivedUrl(url, 400);
}

/** Detail / lightbox sized rendition. */
export function displayUrl(url) {
  return derivedUrl(url, 1024);
}

/**
 * Small avatar rendition. Falls back to the 400px tier when Cloudinary is off,
 * since only 400/1024 are pre-generated in the bucket.
 */
export function avatarUrl(url) {
  return CLOUD_NAME ? derivedUrl(url, 200) : derivedUrl(url, 400);
}

/**
 * onError handler that falls back to the original once, so an image that
 * cannot be transformed still renders instead of breaking.
 *
 * Usage: <img src={thumbUrl(item.url)} onError={imgFallback(item.url)} />
 */
export function imgFallback(originalUrl) {
  return (event) => {
    const el = event.currentTarget || event.target;
    if (!el || !originalUrl) return;
    if (el.dataset && el.dataset.fellBack === "1") return; // avoid a loop
    if (el.dataset) el.dataset.fellBack = "1";
    el.src = originalUrl;
  };
}
